import React, { useRef, useEffect, useCallback } from 'react';
import { useState } from 'react';
import { ChevronLeft, ChevronRight, Pencil, ChevronDown, Loader2, Eye, EyeOff, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getEventCategory, EVENT_CATEGORIES, EventCategory } from '@/lib/event-type-icons';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  EventTypeIcon,
  EVENT_TYPE_LABELS,
  CATEGORY_COLORS,
} from './EventTypeBadge';

// Event type options grouped by category
const EVENT_TYPE_OPTIONS = {
  pricing: ['price_increased', 'price_decreased', 'price_hidden', 'price_revealed', 'discount_added', 'discount_removed', 'discount_changed', 'pricing_restructured'],
  packaging: ['plan_added', 'plan_removed', 'plan_renamed', 'pricing_metric_changed', 'threshold_added', 'threshold_removed', 'capacity_increased', 'capacity_decreased', 'trial_changed', 'freemium_changed', 'addon_added', 'addon_removed', 'addon_changed'],
  product: ['feature_added', 'feature_removed', 'feature_changed', 'positioning_changed'],
};

// Reusable EventTypeSelect component
interface EventTypeSelectProps {
  value: string;
  onValueChange: (value: string) => void;
  disabled?: boolean;
  triggerClassName?: string;
  onTriggerClick?: (e: React.MouseEvent) => void;
}

const EventTypeSelect: React.FC<EventTypeSelectProps> = ({
  value,
  onValueChange,
  disabled = false,
  triggerClassName,
  onTriggerClick,
}) => (
  <Select value={value} onValueChange={onValueChange} disabled={disabled}>
    <SelectTrigger className={triggerClassName} onClick={onTriggerClick}>
      <ChevronDown className="h-4 w-4 text-gray-700 flex-shrink-0" />
    </SelectTrigger>
    <SelectContent>
      <SelectGroup>
        <SelectLabel>Pricing</SelectLabel>
        {EVENT_TYPE_OPTIONS.pricing.map(type => (
          <SelectItem key={type} value={type} className="text-xs">
            {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </SelectItem>
        ))}
      </SelectGroup>
      <SelectGroup>
        <SelectLabel>Packaging</SelectLabel>
        {EVENT_TYPE_OPTIONS.packaging.map(type => (
          <SelectItem key={type} value={type} className="text-xs">
            {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </SelectItem>
        ))}
      </SelectGroup>
      <SelectGroup>
        <SelectLabel>Product</SelectLabel>
        {EVENT_TYPE_OPTIONS.product.map(type => (
          <SelectItem key={type} value={type} className="text-xs">
            {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </SelectItem>
        ))}
      </SelectGroup>
    </SelectContent>
  </Select>
);

// Event type for the ribbon
export interface DiffEvent {
  id?: string;
  type: string;
  description: string;
  invalid: boolean;
  created_at: string;
  updated_at: string;
  url?: string;
  images?: {
    before_cropped?: string;
    after_cropped?: string;
    before_annotated?: string;
    after_annotated?: string;
  };
  viewport1?: {
    panX: number;
    panY: number;
    zoom: number;
  };
  viewport2?: {
    panX: number;
    panY: number;
    zoom: number;
  };
  before_marker?: {
    x: number;
    y: number;
  } | null;
  after_marker?: {
    x: number;
    y: number;
  } | null;
}

// Group events by category with indices
interface GroupedEvent {
  event: DiffEvent;
  originalIndex: number;
}

interface CategoryGroup {
  category: EventCategory;
  label: string;
  events: GroupedEvent[];
}

function groupEventsByCategory(events: DiffEvent[]): CategoryGroup[] {
  const categoryOrder: EventCategory[] = ['pricing', 'packaging', 'product', 'other'];
  const groups: Record<EventCategory, GroupedEvent[]> = {
    pricing: [],
    packaging: [],
    product: [],
    other: [],
  };

  events.forEach((event, index) => {
    const category = getEventCategory(event.type);
    groups[category].push({ event, originalIndex: index });
  });

  return categoryOrder
    .filter(cat => groups[cat].length > 0)
    .map(cat => ({
      category: cat,
      label: EVENT_CATEGORIES[cat].label,
      events: groups[cat],
    }));
}

interface EventRibbonProps {
  events: DiffEvent[];
  selectedEventIndex: number;
  onEventSelect: (index: number) => void;
  isEditingDescription?: boolean;
  editedDescription?: string;
  onDescriptionChange?: (value: string) => void;
  onSaveDescription?: () => void;
  onCancelDescription?: () => void;
  onStartEditing?: () => void;
  savingDescription?: boolean;
  onEventTypeChange?: (newType: string) => void;
  updatingEventType?: boolean;
  slug?: string;
  period?: string;
  onMarkInvalid?: (eventId: string, invalid: boolean) => Promise<void>;
  showInvalidEvents?: boolean;
  updatingInvalid?: boolean;
  onToggleShowInvalid?: () => void;
  onOpenWebsite?: () => void;
  onOpenPricingPage?: () => void;
  pricingPageUrl?: string | null;
}

export function EventRibbon({
  events,
  selectedEventIndex,
  onEventSelect,
  isEditingDescription = false,
  editedDescription = '',
  onDescriptionChange,
  onSaveDescription,
  onCancelDescription,
  onStartEditing,
  savingDescription = false,
  onEventTypeChange,
  updatingEventType = false,
  slug,
  period,
  onMarkInvalid,
  showInvalidEvents = true,
  updatingInvalid = false,
  onToggleShowInvalid,
  onOpenWebsite,
  onOpenPricingPage,
  pricingPageUrl,
}: EventRibbonProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const ribbonContainerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<number, HTMLButtonElement>>(new Map());
  const [canScroll, setCanScroll] = useState(false);
  const [caretLeft, setCaretLeft] = useState<number | null>(null);

  // Filter invalid events when showInvalidEvents=false
  const visibleEvents = showInvalidEvents
    ? events
    : events.filter(e => !e.invalid);

  // Check if there are any invalid events
  const hasInvalidEvents = events.some(e => e.invalid);

  const groupedEvents = groupEventsByCategory(visibleEvents);
  const selectedEvent = visibleEvents[selectedEventIndex];
  const selectedCategory = selectedEvent ? getEventCategory(selectedEvent.type) : 'other';
  const categoryColors = CATEGORY_COLORS[selectedCategory];

  // Calculate caret position based on selected button
  useEffect(() => {
    const itemEl = itemRefs.current.get(selectedEventIndex);
    const containerEl = ribbonContainerRef.current;
    if (itemEl && containerEl) {
      const itemRect = itemEl.getBoundingClientRect();
      const containerRect = containerEl.getBoundingClientRect();
      // Center the caret under the button
      const buttonCenter = itemRect.left + itemRect.width / 2 - containerRect.left;
      setCaretLeft(buttonCenter);
    }
  }, [selectedEventIndex]);

  // Check if scrolling is needed
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const checkScroll = () => {
      setCanScroll(container.scrollWidth > container.clientWidth);
    };

    checkScroll();
    const resizeObserver = new ResizeObserver(checkScroll);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [visibleEvents]);

  // Scroll selected item into view
  useEffect(() => {
    const itemEl = itemRefs.current.get(selectedEventIndex);
    if (itemEl && scrollContainerRef.current) {
      itemEl.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center',
      });
    }
  }, [selectedEventIndex]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    const flatIndices = groupedEvents.flatMap(g => g.events.map(e => e.originalIndex));
    const currentPos = flatIndices.indexOf(selectedEventIndex);

    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (currentPos > 0) {
        onEventSelect(flatIndices[currentPos - 1]);
      }
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault();
      if (currentPos < flatIndices.length - 1) {
        onEventSelect(flatIndices[currentPos + 1]);
      }
    } else if (e.key === 'Home') {
      e.preventDefault();
      if (flatIndices.length > 0) {
        onEventSelect(flatIndices[0]);
      }
    } else if (e.key === 'End') {
      e.preventDefault();
      if (flatIndices.length > 0) {
        onEventSelect(flatIndices[flatIndices.length - 1]);
      }
    }
  }, [groupedEvents, selectedEventIndex, onEventSelect]);

  // Scroll controls
  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 200;
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  if (visibleEvents.length === 0) return null;

  return (
    <div className="flex flex-col" ref={ribbonContainerRef}>
      {/* Header with Slug, Period, and Website Button */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {slug && <span className="text-sm font-semibold text-gray-900">{slug}</span>}
          {period && <span className="text-xs text-muted-foreground px-2 py-0.5 bg-gray-100 rounded">{period}</span>}
        </div>

        <div className="flex items-center gap-2">
          {/* View Pricing Page Button */}
          {onOpenPricingPage && pricingPageUrl && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onOpenPricingPage}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    <span className="text-sm">Pricing Page</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{pricingPageUrl}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {/* View on Website Button */}
          {onOpenWebsite && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onOpenWebsite}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    <span className="text-sm">View on Website</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Open in PricingSaaS Website</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>

      {/* Ribbon Container */}
      <div className="relative group">
        {/* Left scroll button */}
        {canScroll && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 h-8 w-8 bg-background/90 backdrop-blur-sm shadow-md opacity-0 group-hover:opacity-100 transition-opacity rounded-full border"
            onClick={() => scroll('left')}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
        )}

        {/* Scrollable ribbon */}
        <div
          ref={scrollContainerRef}
          className={cn(
            "flex items-stretch gap-3 overflow-x-auto scrollbar-hide py-2",
            canScroll ? "px-10" : "px-0"
          )}
          onKeyDown={handleKeyDown}
          tabIndex={0}
          role="listbox"
          aria-label="Event navigation"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {/* Category Cards */}
          {groupedEvents.map((group) => {
            const colors = CATEGORY_COLORS[group.category];

            return (
              <div
                key={`card-${group.category}`}
                className={cn(
                  "flex flex-col rounded-md border p-3 flex-shrink-0",
                  colors.border,
                  "bg-gradient-to-b from-background to-muted/20"
                )}
              >
                {/* Category Header */}
                <div className="flex items-center gap-2 mb-2 px-1">
                  <div className={cn("w-2 h-2 rounded-full", colors.accent)} />
                  <span className={cn(
                    "text-[11px] font-semibold uppercase tracking-wider",
                    colors.text
                  )}>
                    {group.label}
                  </span>
                  <div className={cn("flex-1 h-px", colors.accent, "opacity-30")} />
                </div>

                {/* Events in this category */}
                <div className="flex items-stretch gap-1">
                  {group.events.map(({ event, originalIndex }) => {
                    const isActive = originalIndex === selectedEventIndex;
                    const eventColors = CATEGORY_COLORS[getEventCategory(event.type)];

                    return (
                      <div
                        key={`ribbon-${event.type}-${originalIndex}`}
                        className="relative group/card"
                      >
                        <button
                          ref={(el) => {
                            if (el) itemRefs.current.set(originalIndex, el);
                          }}
                          onClick={() => onEventSelect(originalIndex)}
                          onKeyDown={handleKeyDown}
                          role="option"
                          aria-selected={isActive}
                          className={cn(
                            "flex items-center gap-2 px-3 py-2 rounded transition-all flex-shrink-0",
                            "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1",
                            isActive
                              ? "shadow-sm border-2"
                              : "hover:bg-muted/60",
                            event.invalid && "opacity-50"
                          )}
                          style={isActive ? {
                            borderColor: `${eventColors.accentHex}50`,
                            backgroundColor: `${eventColors.accentHex}10`,
                          } : undefined}
                        >
                          {/* Icon */}
                          <EventTypeIcon eventType={event.type} className={cn(
                            "flex-shrink-0 transition-all",
                            isActive ? "w-7 h-7" : "w-6 h-6"
                          )} />

                          {/* Label */}
                          <span className={cn(
                            "text-sm font-medium whitespace-nowrap",
                            isActive ? "text-foreground" : "text-muted-foreground"
                          )}>
                            {EVENT_TYPE_LABELS[event.type] || event.type}
                          </span>
                        </button>

                        {/* Event Type Dropdown - appears on active event */}
                        {onEventTypeChange && isActive && (
                          <div className="absolute -top-1 -right-1 z-10">
                            <EventTypeSelect
                              value={event.type || ''}
                              onValueChange={(newType) => {
                                if (originalIndex !== selectedEventIndex) {
                                  onEventSelect(originalIndex);
                                }
                                onEventTypeChange(newType);
                              }}
                              disabled={updatingEventType}
                              triggerClassName="h-6 w-6 p-0 border bg-white hover:bg-gray-100 rounded-full shadow-sm flex items-center justify-center [&[data-state=open]>svg:last-child]:hidden [&>svg:last-child]:hidden"
                              onTriggerClick={(e) => {
                                e.stopPropagation();
                                if (originalIndex !== selectedEventIndex) {
                                  onEventSelect(originalIndex);
                                }
                              }}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Show/Hide Invalid Events Tab - Only show if there are invalid events */}
          {hasInvalidEvents && onToggleShowInvalid && (
            <div
              className={cn(
                "flex flex-col rounded-md border p-3 flex-shrink-0",
                "border-gray-300",
                "bg-gradient-to-b from-background to-muted/20"
              )}
            >
              {/* Header */}
              <div className="flex items-center gap-2 mb-2 px-1">
                <div className="w-2 h-2 rounded-full bg-gray-400" />
                <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-600">
                  View
                </span>
                <div className="flex-1 h-px bg-gray-400 opacity-30" />
              </div>

              {/* Toggle Button */}
              <div className="flex items-stretch gap-1">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={onToggleShowInvalid}
                        className={cn(
                          "flex items-center gap-2 px-3 py-2 rounded transition-all flex-shrink-0",
                          "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1",
                          showInvalidEvents
                            ? "bg-gray-100 border-2 border-gray-300"
                            : "hover:bg-muted/60"
                        )}
                      >
                        {showInvalidEvents ? (
                          <Eye className="w-6 h-6 flex-shrink-0 text-gray-700" />
                        ) : (
                          <EyeOff className="w-6 h-6 flex-shrink-0 text-gray-700" />
                        )}
                        <span className="text-sm font-medium whitespace-nowrap text-gray-900">
                          {showInvalidEvents ? 'Hide Invalid' : 'Show Invalid'}
                        </span>
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{showInvalidEvents ? 'Hide' : 'Show'} Invalid Events</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          )}
        </div>

        {/* Right scroll button */}
        {canScroll && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 h-8 w-8 bg-background/90 backdrop-blur-sm shadow-md opacity-0 group-hover:opacity-100 transition-opacity rounded-full border"
            onClick={() => scroll('right')}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Connected Description Panel with Caret */}
      {selectedEvent && (
        <div className="relative mt-2">
          {/* Caret pointing up - dynamically positioned */}
          {caretLeft !== null && (
            <div
              className="absolute -top-2 w-4 h-4 rotate-45 border-l-2 border-t-2 z-10"
              style={{
                left: caretLeft - 8, // center the 16px caret
                borderColor: `${categoryColors.accentHex}50`,
                backgroundColor: `${categoryColors.accentHex}10`,
              }}
            />
          )}

          {/* Panel */}
          <div
            className={cn(
              "rounded-lg p-4 transition-colors duration-200 border-2",
              categoryColors.bg
            )}
            style={{
              borderColor: `${categoryColors.accentHex}50`,
            }}
          >
            <div className="flex items-start gap-3">
              {/* Large icon */}
              <EventTypeIcon eventType={selectedEvent.type} className="w-10 h-10 flex-shrink-0" />

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <div className="flex items-center gap-1">
                    <span className={cn(
                      "text-sm font-semibold",
                      categoryColors.text
                    )}>
                      {EVENT_TYPE_LABELS[selectedEvent.type] || selectedEvent.type}
                    </span>
                    {/* Event Type Dropdown */}
                    {onEventTypeChange && (
                      <EventTypeSelect
                        value={selectedEvent.type || ''}
                        onValueChange={onEventTypeChange}
                        disabled={updatingEventType}
                        triggerClassName="h-5 w-5 p-0 border-0 bg-transparent hover:bg-gray-100 rounded flex items-center justify-center [&[data-state=open]>svg:last-child]:hidden [&>svg:last-child]:hidden"
                        onTriggerClick={(e) => e.stopPropagation()}
                      />
                    )}
                  </div>
                  <span className={cn(
                    "text-[10px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded",
                    categoryColors.text,
                    "bg-white/50 dark:bg-black/20"
                  )}>
                    {EVENT_CATEGORIES[selectedCategory].label}
                  </span>
                  {/* Mark Invalid Button */}
                  {onMarkInvalid && selectedEvent.id && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onMarkInvalid(selectedEvent.id!, !selectedEvent.invalid)}
                      disabled={updatingInvalid}
                      className="ml-auto"
                    >
                      {updatingInvalid ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : null}
                      {selectedEvent.invalid ? 'Mark Valid' : 'Mark Invalid'}
                    </Button>
                  )}
                </div>

                {/* Description with inline editing */}
                {isEditingDescription ? (
                  <div className="space-y-2">
                    <textarea
                      value={editedDescription}
                      onChange={(e) => onDescriptionChange?.(e.target.value)}
                      className="w-full text-sm border rounded px-2 py-1 min-h-[60px] resize-y text-gray-900"
                      autoFocus
                      disabled={savingDescription}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.ctrlKey) {
                          onSaveDescription?.();
                        } else if (e.key === 'Escape') {
                          onCancelDescription?.();
                        }
                      }}
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={onSaveDescription}
                        disabled={savingDescription}
                        className="text-xs px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
                      >
                        {savingDescription ? 'Saving...' : 'Save (Ctrl+Enter)'}
                      </button>
                      <button
                        onClick={onCancelDescription}
                        disabled={savingDescription}
                        className="text-xs px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50"
                      >
                        Cancel (Esc)
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-2 group">
                    <p
                      className="flex-1 text-sm text-foreground leading-relaxed cursor-pointer hover:bg-gray-100 rounded px-2 py-1 -mx-2"
                      onClick={onStartEditing}
                      title="Click to edit description"
                    >
                      {selectedEvent.description}
                    </p>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={onStartEditing}
                      title="Edit description"
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
